import React, { useRef } from 'react';
import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import { styled } from '@mui/material/styles';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

/**
 * Image Carousel Component
 * Uses Swiper for smooth touch-enabled carousel
 */
const CarouselContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100%',
  
  '& .swiper': {
    width: '100%',
    height: '100%',
  },
  
  '& .swiper-slide': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  '& .swiper-pagination-bullet': {
    backgroundColor: '#fff',
    opacity: 0.7,
  },
  
  '& .swiper-pagination-bullet-active': {
    opacity: 1,
    backgroundColor: '#003B95',
  },
});

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  zIndex: 10,
  transition: 'all 0.3s ease-in-out',
  
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    transform: 'translateY(-50%) scale(1.1)',
  },
  
  '&.swiper-button-disabled': {
    opacity: 0.3,
    pointerEvents: 'none',
  },
}));

const ImageCarousel = ({
  images = [],
  autoplay = false,
  autoplayDelay = 3000,
  loop = true,
  effect = 'slide', // 'slide', 'fade', 'cube', 'coverflow', 'flip'
  showNavigation = true,
  showPagination = true,
  spaceBetween = 0,
  slidesPerView = 1,
  height = 400,
}) => {
  const swiperRef = useRef(null);

  const handlePrev = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext();
    }
  };

  const swiperModules = [Navigation, Pagination];
  if (autoplay) swiperModules.push(Autoplay);
  if (effect === 'fade') swiperModules.push(EffectFade);

  return (
    <CarouselContainer sx={{ height }}>
      <Swiper
        ref={swiperRef}
        modules={swiperModules}
        spaceBetween={spaceBetween}
        slidesPerView={slidesPerView}
        loop={loop}
        effect={effect}
        autoplay={
          autoplay
            ? {
                delay: autoplayDelay,
                disableOnInteraction: false,
              }
            : false
        }
        pagination={
          showPagination
            ? {
                clickable: true,
                dynamicBullets: true,
              }
            : false
        }
        style={{ width: '100%', height: '100%' }}
      >
        {images.map((image, index) => (
          <SwiperSlide key={index}>
            <Box
              component="img"
              src={typeof image === 'string' ? image : image.src}
              alt={typeof image === 'object' ? image.alt : `Slide ${index + 1}`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {showNavigation && images.length > 1 && (
        <>
          <NavigationButton
            onClick={handlePrev}
            sx={{ left: 16 }}
            size="small"
          >
            <ChevronLeft />
          </NavigationButton>
          <NavigationButton
            onClick={handleNext}
            sx={{ right: 16 }}
            size="small"
          >
            <ChevronRight />
          </NavigationButton>
        </>
      )}
    </CarouselContainer>
  );
};

export default ImageCarousel;
